import React, { useState, useContext } from "react";
import axios from "axios";
import {
  FiCheck, FiAlertCircle, FiUpload,
  FiShield, FiMapPin, FiUser, FiCreditCard, FiLock, FiInfo,
  FiX, FiImage, FiCamera
} from "react-icons/fi";
import { AuthContext } from "../context/AuthContext";

/* ═══════════════════════════════════════════
   STYLES — Montserrat · Navy + Amber
═══════════════════════════════════════════ */
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');

  .kyc-root {
    font-family: 'Montserrat', sans-serif;
    background: #f0f4f8; min-height: 100%;
  }
  .kyc-root *, .kyc-root *::before, .kyc-root *::after { box-sizing: border-box; }
  .kyc-root ::-webkit-scrollbar { width: 4px; }
  .kyc-root ::-webkit-scrollbar-track { background: transparent; }
  .kyc-root ::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 99px; }

  .kyc-page {
    max-width: 860px; margin: 0 auto;
    padding: 24px 20px;
    display: flex; flex-direction: column; gap: 20px;
  }
  @media (max-width: 600px) { .kyc-page { padding: 14px 12px; gap: 14px; } }

  /* ══════════════════════════════
     HERO
  ══════════════════════════════ */
  .kyc-hero {
    background: linear-gradient(135deg, #0a1628 0%, #0f2040 55%, #162a52 100%);
    border-radius: 22px; padding: 28px;
    display: flex; align-items: center; gap: 20px;
    position: relative; overflow: hidden; flex-wrap: wrap;
  }
  .kyc-hero::before {
    content: ''; position: absolute; top: -80px; right: -60px;
    width: 260px; height: 260px; border-radius: 50%;
    background: radial-gradient(circle, rgba(251,191,36,.16) 0%, transparent 70%);
    pointer-events: none;
  }
  .kyc-hero-icon {
    width: 60px; height: 60px; border-radius: 17px; flex-shrink: 0;
    background: linear-gradient(135deg, #fbbf24, #f59e0b);
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 0 0 5px rgba(251,191,36,.15), 0 8px 24px rgba(251,191,36,.2);
    position: relative; z-index: 1;
  }
  .kyc-hero-left { position: relative; z-index: 1; }
  .kyc-hero-badge {
    display: inline-flex; align-items: center; gap: 6px;
    background: rgba(251,191,36,.15); border: 1px solid rgba(251,191,36,.3);
    border-radius: 99px; padding: 4px 12px;
    font-size: 11px; font-weight: 700; color: #fcd34d;
    letter-spacing: .07em; text-transform: uppercase; margin-bottom: 8px;
  }
  .kyc-badge-dot {
    width: 6px; height: 6px; border-radius: 50%; background: #fbbf24;
    animation: kyc-pulse 2s infinite;
  }
  @keyframes kyc-pulse { 0%,100%{opacity:1} 50%{opacity:.35} }
  .kyc-hero-title {
    font-size: clamp(18px, 3.5vw, 24px); font-weight: 900;
    color: #fff; line-height: 1.2; margin-bottom: 4px;
  }
  .kyc-hero-sub { font-size: 13px; font-weight: 500; color: #64748b; }

  /* ══════════════════════════════
     STEP INDICATORS
  ══════════════════════════════ */
  .kyc-steps {
    display: flex; align-items: center; gap: 0;
    background: #fff; border-radius: 16px; padding: 16px 20px;
    border: 1px solid #e2e8f0; box-shadow: 0 1px 4px rgba(0,0,0,.05);
    overflow-x: auto;
  }
  .kyc-step {
    display: flex; align-items: center; gap: 8px; flex-shrink: 0;
  }
  .kyc-step-circle {
    width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    font-size: 12px; font-weight: 900; transition: all .2s;
  }
  .kyc-step-circle.done   { background: linear-gradient(135deg,#10b981,#059669); color: #fff; }
  .kyc-step-circle.active { background: linear-gradient(135deg,#0f2040,#162a52); color: #fbbf24; box-shadow: 0 0 0 3px rgba(251,191,36,.2); }
  .kyc-step-circle.idle   { background: #f1f5f9; color: #94a3b8; border: 2px solid #e2e8f0; }
  .kyc-step-label {
    font-size: 11px; font-weight: 700; white-space: nowrap;
  }
  .kyc-step-label.active { color: #0f172a; }
  .kyc-step-label.idle   { color: #94a3b8; }
  .kyc-step-label.done   { color: #059669; }
  .kyc-step-connector {
    flex: 1; height: 2px; min-width: 20px; margin: 0 6px;
    border-radius: 99px; background: #e2e8f0;
  }
  .kyc-step-connector.done { background: linear-gradient(90deg,#10b981,#059669); }

  /* ══════════════════════════════
     MAIN FORM CARD
  ══════════════════════════════ */
  .kyc-card {
    background: #fff; border-radius: 20px; padding: 28px;
    border: 1px solid #e2e8f0; box-shadow: 0 1px 4px rgba(0,0,0,.05);
  }
  @media (max-width: 600px) { .kyc-card { padding: 18px 16px; } }

  /* ── Alerts ── */
  .kyc-alert {
    display: flex; align-items: flex-start; gap: 10px;
    padding: 13px 16px; border-radius: 13px;
    font-size: 13px; font-weight: 600; margin-bottom: 20px;
  }
  .kyc-alert.error   { background: #fef2f2; border: 1px solid #fecaca; color: #b91c1c; }
  .kyc-alert.success { background: #f0fdf4; border: 1px solid #bbf7d0; color: #15803d; }
  .kyc-alert svg { flex-shrink: 0; margin-top: 1px; }

  /* ── Info banner ── */
  .kyc-info-banner {
    display: flex; align-items: flex-start; gap: 10px;
    padding: 13px 16px; border-radius: 13px;
    background: linear-gradient(135deg,#eff6ff,#dbeafe);
    border: 1px solid #bfdbfe; margin-bottom: 28px;
    font-size: 12.5px; font-weight: 600; color: #1d4ed8;
  }
  .kyc-info-banner svg { flex-shrink: 0; margin-top: 1px; color: #2563eb; }

  /* ── Section header ── */
  .kyc-section {
    margin-bottom: 24px;
  }
  .kyc-section-header {
    display: flex; align-items: center; gap: 10px; margin-bottom: 18px;
  }
  .kyc-section-icon {
    width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    background: linear-gradient(135deg, #0f2040, #162a52); color: #fbbf24;
  }
  .kyc-section-title {
    font-size: 14px; font-weight: 800; color: #0f172a;
    text-transform: uppercase; letter-spacing: .07em;
  }
  .kyc-section-divider {
    height: 1px; background: linear-gradient(90deg, #fbbf24, transparent);
    margin-top: 4px;
  }

  /* ── Fields ── */
  .kyc-field { margin-bottom: 14px; }
  .kyc-label {
    display: block; font-size: 11px; font-weight: 800; color: #334155;
    text-transform: uppercase; letter-spacing: .07em; margin-bottom: 7px;
  }
  .kyc-required { color: #ef4444; margin-left: 2px; }
  .kyc-optional { font-size: 10px; font-weight: 600; color: #94a3b8; text-transform: none; letter-spacing: 0; margin-left: 4px; }

  .kyc-input, .kyc-select, .kyc-textarea {
    width: 100%; padding: 12px 16px;
    border: 2px solid #e2e8f0; border-radius: 13px;
    font-family: 'Montserrat', sans-serif; font-size: 13.5px; font-weight: 600;
    color: #0f172a; outline: none; background: #fff;
    transition: border-color .2s, box-shadow .2s;
  }
  .kyc-input:focus, .kyc-select:focus, .kyc-textarea:focus {
    border-color: #fbbf24; box-shadow: 0 0 0 3px rgba(251,191,36,.12);
  }
  .kyc-input::placeholder, .kyc-textarea::placeholder { color: #94a3b8; font-weight: 500; }
  .kyc-input.mono { font-family: 'Montserrat', sans-serif; letter-spacing: .1em; }
  .kyc-select {
    appearance: none; cursor: pointer;
    background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E") no-repeat right 14px center;
    padding-right: 38px;
  }
  .kyc-textarea { resize: vertical; min-height: 88px; }

  /* two-col grid */
  .kyc-two-col {
    display: grid; grid-template-columns: 1fr 1fr; gap: 14px;
  }
  @media (max-width: 560px) { .kyc-two-col { grid-template-columns: 1fr; } }

  /* File Upload Styles */
  .kyc-upload-section {
    margin-bottom: 20px;
  }
  
  .kyc-upload-label {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 140px;
    border-radius: 14px;
    border: 2px dashed #e2e8f0;
    cursor: pointer;
    background: #f8fafc;
    transition: border-color .2s, background .2s;
    position: relative;
    overflow: hidden;
  }
  
  .kyc-upload-label:hover {
    border-color: #fbbf24;
    background: #fffbeb;
  }
  
  .kyc-upload-label.has-file {
    border-color: #10b981;
    background: #f0fdf4;
    border-style: solid;
  }
  
  .kyc-upload-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 20px;
    text-align: center;
  }
  
  .kyc-upload-icon {
    color: #94a3b8;
    transition: color .2s;
  }
  
  .kyc-upload-label:hover .kyc-upload-icon {
    color: #f59e0b;
  }
  
  .kyc-upload-text {
    font-size: 13px;
    font-weight: 700;
    color: #475569;
  }
  
  .kyc-upload-hint {
    font-size: 11px;
    font-weight: 600;
    color: #94a3b8;
  }
  
  .kyc-upload-preview {
    width: 100%;
    height: 140px;
    object-fit: contain;
    border-radius: 10px;
    padding: 8px;
  }
  
  .kyc-upload-remove {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: rgba(239,68,68,.9);
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    transition: background .2s;
  }
  
  .kyc-upload-remove:hover {
    background: #dc2626;
  }
  
  .kyc-filename {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
    margin-top: 6px;
    text-align: center;
  }
  
  .kyc-upload-loading {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    color: #f59e0b;
  }

  /* ── Security notice ── */
  .kyc-notice {
    display: flex; align-items: flex-start; gap: 10px;
    padding: 14px 16px; border-radius: 13px;
    background: linear-gradient(135deg, #fffbeb, #fef9c3);
    border: 1px solid #fde68a; margin-top: 6px; margin-bottom: 20px;
    font-size: 12.5px; font-weight: 600; color: #92400e;
  }
  .kyc-notice svg { flex-shrink: 0; margin-top: 1px; color: #d97706; }

  /* ── Submit button ── */
  .kyc-submit-btn {
    width: 100%; padding: 15px;
    border: none; border-radius: 14px; cursor: pointer;
    font-family: 'Montserrat', sans-serif; font-size: 14px; font-weight: 900;
    display: flex; align-items: center; justify-content: center; gap: 9px;
    transition: all .22s; letter-spacing: .04em;
  }
  .kyc-submit-btn.ready {
    background: linear-gradient(135deg, #0f2040, #162a52);
    color: #fbbf24; box-shadow: 0 4px 18px rgba(15,32,64,.25);
  }
  .kyc-submit-btn.ready:hover {
    transform: translateY(-2px); box-shadow: 0 8px 24px rgba(15,32,64,.35);
  }
  .kyc-submit-btn.loading { background: #e2e8f0; color: #94a3b8; cursor: not-allowed; }
  .kyc-submit-btn.loading svg { animation: kyc-spin .7s linear infinite; }
  @keyframes kyc-spin { to { transform: rotate(360deg); } }

  /* ── Field hint ── */
  .kyc-hint { font-size: 11px; font-weight: 600; color: #94a3b8; margin-top: 5px; }
`;

/* ═══════════════════════════════════════════
   DATA
═══════════════════════════════════════════ */
const INDIAN_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh",
  "Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand",
  "Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur",
  "Meghalaya","Mizoram","Nagaland","Odisha","Punjab",
  "Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura",
  "Uttar Pradesh","Uttarakhand","West Bengal"
]; // <--- Added missing comment closing

/* ================================
   MAIN COMPONENT
   ================================ */
const RegisterKYC = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [success, setSuccess] = useState("");
  const [step, setStep]       = useState(1); // 1: contact, 2: identity, 3: bank

  const [formData, setFormData] = useState({
    email: "", pan: "", aadhaar: "",
    bankAccount: "", ifsc: "", bankName: "",
    state: "", district: "", tahsil: "",
    postOffice: "", pinCode: "", address: "",
    nomineeName: "", nomineeRelation: "", nomineePhone: ""
  });

  // File upload states
  const [files, setFiles] = useState({
    profilePic: null,
    panFile: null,
    aadhaarFront: null,
    aadhaarBack: null,
    bankFile: null
  });
  
  const [filePreviews, setFilePreviews] = useState({
    profilePic: null,
    panFile: null,
    aadhaarFront: null,
    aadhaarBack: null,
    bankFile: null
  });
  
  const [uploadingFiles, setUploadingFiles] = useState({
    profilePic: false,
    panFile: false,
    aadhaarFront: false,
    aadhaarBack: false,
    bankFile: false
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    const shouldUppercase = ["pan", "aadhaar", "bankAccount", "ifsc", "pinCode", "nomineePhone"].includes(name);

    setFormData(prev => ({
      ...prev,
      [name]: shouldUppercase ? value.toUpperCase() : value
    }));
    setError("");
  };

  // Cloudinary upload function
  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);
    
    const uploadRes = await fetch(
      `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`,
      { method: "POST", body: formData }
    );
    
    if (!uploadRes.ok) throw new Error("Cloudinary upload failed");
    const uploadData = await uploadRes.json();
    
    if (!uploadData.secure_url) {
      throw new Error("Failed to get image URL from Cloudinary");
    }
    
    return uploadData.secure_url;
  };

  // Handle file change
  const handleFileChange = async (fileType, file) => {
    if (!file) return;
    
    // Validate file
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      setError("Only JPG, PNG, WEBP images are allowed");
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      setError("File size must be under 5MB");
      return;
    }
    
    setError("");
    
    try {
      // Set uploading state
      setUploadingFiles(prev => ({ ...prev, [fileType]: true }));
      
      // Upload to Cloudinary
      const imageUrl = await uploadToCloudinary(file);
      
      // Update state
      setFiles(prev => ({ ...prev, [fileType]: file }));
      setFilePreviews(prev => ({ ...prev, [fileType]: URL.createObjectURL(file) }));
      
      // Store URL in form data
      setFormData(prev => ({ ...prev, [fileType]: imageUrl }));
      
    } catch (error) {
      console.error("File upload error:", error);
      setError(`Failed to upload ${fileType}: ${error.message}`);
    } finally {
      setUploadingFiles(prev => ({ ...prev, [fileType]: false }));
    }
  };

  // Remove file
  const removeFile = (fileType) => {
    setFiles(prev => ({ ...prev, [fileType]: null }));
    setFilePreviews(prev => ({ ...prev, [fileType]: null }));
    setFormData(prev => ({ ...prev, [fileType]: "" }));
  };

  const validateForm = () => {
    if (!formData.email)        return "Email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return "Invalid email format";
    if (!formData.pan)          return "PAN number is required";
    if (!/^[A-Z0-9]{10}$/.test(formData.pan)) return "PAN must be 10 alphanumeric characters";
    if (!formData.aadhaar)      return "Aadhaar number is required";
    if (!/^\d{12}$/.test(formData.aadhaar)) return "Aadhaar must be exactly 12 digits";
    if (!formData.bankName)      return "Bank name is required";
    if (!formData.bankAccount)  return "Bank account number is required";
    if (formData.bankAccount.length < 10) return "Bank account must be at least 10 digits";
    if (!formData.ifsc)         return "IFSC code is required";
    if (!/^[A-Z0-9]{11}$/.test(formData.ifsc)) return "IFSC must be 11 alphanumeric characters";
    if (!formData.state)        return "State is required";
    if (!formData.district)     return "District is required";
    if (!formData.tahsil)       return "Tahsil is required";
    if (!formData.postOffice)   return "Post Office is required";
    if (!formData.pinCode)      return "Pin Code is required";
    if (!formData.nomineeName)  return "Nominee name is required";
    if (!formData.nomineeRelation) return "Nominee relation is required";
    if (!formData.nomineePhone) return "Nominee phone is required";
    if (!/^\d{10}$/.test(formData.nomineePhone)) return "Nominee phone must be 10 digits";
    // File validations
    if (!formData.profilePic)   return "Profile picture is required";
    if (!formData.panFile)     return "PAN card is required";
    if (!formData.aadhaarFront) return "Aadhaar front is required";
    if (!formData.aadhaarBack)  return "Aadhaar back is required";
    if (!formData.bankFile)    return "Bank passbook is required";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setSuccess("");
    const err = validateForm();
    if (err) { setError(err); return; }
    setLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/register-kyc/${user.id}`,
        formData
      );
      setSuccess(res.data.message);
      setStep(3);
      setFormData({ 
        email:"", pan:"", aadhaar:"", bankAccount:"", ifsc:"", bankName:"", 
        state:"", district:"", tahsil:"", postOffice:"", pinCode:"", address:"",
        nomineeName:"", nomineeRelation:"", nomineePhone:"",
        profilePic:"", panFile:"", aadhaarFront:"", aadhaarBack:"", bankFile:""
      });
      setFiles({ profilePic: null, panFile: null, aadhaarFront: null, aadhaarBack: null, bankFile: null });
      setFilePreviews({ profilePic: null, panFile: null, aadhaarFront: null, aadhaarBack: null, bankFile: null });
    } catch (err) {
      setError(err.response?.data?.message || "KYC upload failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const STEPS = [
    { n: 1, label: "Contact & Location" },
    { n: 2, label: "Identity"           },
    { n: 3, label: "Bank Details"       },
  ];

  return (
    <div className="kyc-root">
      <style>{styles}</style>
      <div className="kyc-page">

        {/* ── Hero ── */}
        <div className="kyc-hero">
          <div className="kyc-hero-icon">
            <FiShield size={26} color="#0f172a" />
          </div>
          <div className="kyc-hero-left">
            <div className="kyc-hero-badge"><div className="kyc-badge-dot"/>Verification</div>
            <div className="kyc-hero-title">Complete KYC Verification</div>
            <div className="kyc-hero-sub">Upload your documents to activate your account</div>
          </div>
        </div>

        {/* ── Step indicators ── */}
        <div className="kyc-steps">
          {STEPS.map((s, i) => {
            const state = success ? "done" : s.n === step ? "active" : s.n < step ? "done" : "idle";
            return (
              <React.Fragment key={s.n}>
                <div className="kyc-step">
                  <div className={`kyc-step-circle ${state}`}>
                    {state === "done" ? <FiCheck size={14}/> : s.n}
                  </div>
                  <span className={`kyc-step-label ${state}`}>{s.label}</span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`kyc-step-connector ${s.n < step ? "done" : ""}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* ── Form card ── */}
        <div className="kyc-card">

          {/* Alerts */}
          {error   && <div className="kyc-alert error">  <FiAlertCircle size={15}/>{error}  </div>}
          {success && <div className="kyc-alert success"><FiCheck size={15}/>{success}</div>}

          {/* Info banner */}
          {!success && (
            <div className="kyc-info-banner">
              <FiInfo size={15}/>
              Please provide accurate PAN, Aadhaar, and bank details. These will be securely verified by our admin team.
            </div>
          )}

          {!success && (
            <form onSubmit={handleSubmit}>

              {/* ── Section 1: Contact & Location ── */}
              <div className="kyc-section">
                <div className="kyc-section-header">
                  <div className="kyc-section-icon"><FiMapPin size={16}/></div>
                  <div>
                    <div className="kyc-section-title">Contact & Location</div>
                    <div className="kyc-section-divider"/>
                  </div>
                </div>

                <div className="kyc-field">
                  <label className="kyc-label">Email Address <span className="kyc-required">*</span></label>
                  <input
                    type="email" name="email" className="kyc-input"
                    value={formData.email} onChange={handleChange}
                    placeholder="yourname@example.com"
                    onFocus={() => setStep(1)}
                  />
                </div>

                <div className="kyc-field">
                  <label className="kyc-label">State <span className="kyc-required">*</span></label>
                  <select name="state" className="kyc-select" value={formData.state} onChange={handleChange}>
                    <option value="">Select your state</option>
                    {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <div className="kyc-two-col">
                  <div className="kyc-field" style={{ marginBottom: 0 }}>
                    <label className="kyc-label">District <span className="kyc-required">*</span></label>
                    <input type="text" name="district" className="kyc-input"
                      value={formData.district} onChange={handleChange} placeholder="District" />
                  </div>
                  <div className="kyc-field" style={{ marginBottom: 0 }}>
                    <label className="kyc-label">Tahsil <span className="kyc-required">*</span></label>
                    <input type="text" name="tahsil" className="kyc-input"
                      value={formData.tahsil} onChange={handleChange} placeholder="Tahsil" />
                  </div>
                </div>

                <div className="kyc-two-col" style={{ marginTop: 14 }}>
                  <div className="kyc-field" style={{ marginBottom: 0 }}>
                    <label className="kyc-label">Post Office <span className="kyc-required">*</span></label>
                    <input type="text" name="postOffice" className="kyc-input"
                      value={formData.postOffice} onChange={handleChange} placeholder="Post Office" />
                  </div>
                  <div className="kyc-field" style={{ marginBottom: 0 }}>
                    <label className="kyc-label">Pin Code <span className="kyc-required">*</span></label>
                    <input type="text" name="pinCode" className="kyc-input"
                      value={formData.pinCode} onChange={handleChange} placeholder="6-digit Pin Code" maxLength="6" />
                  </div>
                </div>

                <div className="kyc-field" style={{ marginTop: 14 }}>
                  <label className="kyc-label">Full Address <span className="kyc-optional">(optional)</span></label>
                  <textarea name="address" className="kyc-textarea"
                    value={formData.address} onChange={handleChange}
                    placeholder="House no., street, locality..." />
                </div>

                {/* Nominee Details */}
                <div className="kyc-section-header" style={{ marginTop: 24 }}>
                  <div className="kyc-section-icon"><FiUser size={16}/></div>
                  <div>
                    <div className="kyc-section-title">Nominee Details</div>
                    <div className="kyc-section-divider"/>
                  </div>
                </div>

                <div className="kyc-field">
                  <label className="kyc-label">Nominee Name <span className="kyc-required">*</span></label>
                  <input type="text" name="nomineeName" className="kyc-input"
                    value={formData.nomineeName} onChange={handleChange}
                    placeholder="Full name of nominee" />
                </div>

                <div className="kyc-two-col">
                  <div className="kyc-field" style={{ marginBottom: 0 }}>
                    <label className="kyc-label">Relation <span className="kyc-required">*</span></label>
                    <input type="text" name="nomineeRelation" className="kyc-input"
                      value={formData.nomineeRelation} onChange={handleChange}
                      placeholder="e.g. Father, Spouse, Son" />
                  </div>
                  <div className="kyc-field" style={{ marginBottom: 0 }}>
                    <label className="kyc-label">Phone Number <span className="kyc-required">*</span></label>
                    <input type="text" name="nomineePhone" className="kyc-input mono"
                      value={formData.nomineePhone} onChange={handleChange}
                      placeholder="10-digit mobile number" maxLength="10" />
                  </div>
                </div>

                {/* Profile Picture Upload */}
                <div className="kyc-upload-section">
                  <label className="kyc-label">Profile Picture <span className="kyc-required">*</span></label>
                  <label className={`kyc-upload-label ${filePreviews.profilePic ? "has-file" : ""}`}>
                    {filePreviews.profilePic ? (
                      <>
                        <img src={filePreviews.profilePic} alt="Profile Preview" className="kyc-upload-preview" />
                        <button className="kyc-upload-remove" onClick={(e) => { e.preventDefault(); removeFile("profilePic"); }} type="button">
                          <FiX size={13}/>
                        </button>
                      </>
                    ) : uploadingFiles.profilePic ? (
                      <div className="kyc-upload-loading">
                        <FiUpload className="kyc-spin" size={20} />
                        <span>Uploading...</span>
                      </div>
                    ) : (
                      <div className="kyc-upload-placeholder">
                        <FiCamera size={30} className="kyc-upload-icon" />
                        <span className="kyc-upload-text">Click to upload profile picture</span>
                        <span className="kyc-upload-hint">JPG, PNG, WEBP - max 5 MB</span>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/jpg,image/webp"
                      onChange={(e) => handleFileChange("profilePic", e.target.files[0])}
                      className="hidden"
                      style={{ display: "none" }}
                    />
                  </label>
                  {files.profilePic && (
                    <div className="kyc-filename">Photo: {files.profilePic.name}</div>
                  )}
                </div>
              </div>

              {/* ── Section 2: Identity ── */}
              <div className="kyc-section">
                <div className="kyc-section-header">
                  <div className="kyc-section-icon"><FiUser size={16}/></div>
                  <div>
                    <div className="kyc-section-title">Tax & Identity Details</div>
                    <div className="kyc-section-divider"/>
                  </div>
                </div>

                <div className="kyc-field">
                  <label className="kyc-label">PAN Number <span className="kyc-required">*</span></label>
                  <input type="text" name="pan" className="kyc-input mono"
                    value={formData.pan} onChange={e => { handleChange(e); setStep(2); }}
                    placeholder="ABCDE1234F" maxLength="10"
                    onFocus={() => setStep(2)}
                  />
                  <div className="kyc-hint">10 alphanumeric characters, e.g. ABCDE1234F</div>
                </div>

                {/* PAN Card Upload */}
                <div className="kyc-upload-section">
                  <label className="kyc-label">PAN Card <span className="kyc-required">*</span></label>
                  <label className={`kyc-upload-label ${filePreviews.panFile ? "has-file" : ""}`}>
                    {filePreviews.panFile ? (
                      <>
                        <img src={filePreviews.panFile} alt="PAN Preview" className="kyc-upload-preview" />
                        <button className="kyc-upload-remove" onClick={(e) => { e.preventDefault(); removeFile("panFile"); }} type="button">
                          <FiX size={13}/>
                        </button>
                      </>
                    ) : uploadingFiles.panFile ? (
                      <div className="kyc-upload-loading">
                        <FiUpload className="kyc-spin" size={20} />
                        <span>Uploading...</span>
                      </div>
                    ) : (
                      <div className="kyc-upload-placeholder">
                        <FiImage size={30} className="kyc-upload-icon" />
                        <span className="kyc-upload-text">Click to upload PAN card</span>
                        <span className="kyc-upload-hint">JPG, PNG, WEBP - max 5 MB</span>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/jpg,image/webp"
                      onChange={(e) => handleFileChange("panFile", e.target.files[0])}
                      className="hidden"
                      style={{ display: "none" }}
                    />
                  </label>
                  {files.panFile && (
                    <div className="kyc-filename">PAN: {files.panFile.name}</div>
                  )}
                </div>

                <div className="kyc-field">
                  <label className="kyc-label">Aadhaar Number <span className="kyc-required">*</span></label>
                  <input type="text" name="aadhaar" className="kyc-input mono"
                    value={formData.aadhaar} onChange={handleChange}
                    placeholder="12-digit Aadhaar number" maxLength="12"
                    onFocus={() => setStep(2)}
                  />
                  <div className="kyc-hint">Enter 12 numeric digits without spaces or dashes</div>
                </div>

                {/* Aadhaar Front Upload */}
                <div className="kyc-upload-section">
                  <label className="kyc-label">Aadhaar Card (Front) <span className="kyc-required">*</span></label>
                  <label className={`kyc-upload-label ${filePreviews.aadhaarFront ? "has-file" : ""}`}>
                    {filePreviews.aadhaarFront ? (
                      <>
                        <img src={filePreviews.aadhaarFront} alt="Aadhaar Front Preview" className="kyc-upload-preview" />
                        <button className="kyc-upload-remove" onClick={(e) => { e.preventDefault(); removeFile("aadhaarFront"); }} type="button">
                          <FiX size={13}/>
                        </button>
                      </>
                    ) : uploadingFiles.aadhaarFront ? (
                      <div className="kyc-upload-loading">
                        <FiUpload className="kyc-spin" size={20} />
                        <span>Uploading...</span>
                      </div>
                    ) : (
                      <div className="kyc-upload-placeholder">
                        <FiImage size={30} className="kyc-upload-icon" />
                        <span className="kyc-upload-text">Click to upload Aadhaar front</span>
                        <span className="kyc-upload-hint">JPG, PNG, WEBP - max 5 MB</span>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/jpg,image/webp"
                      onChange={(e) => handleFileChange("aadhaarFront", e.target.files[0])}
                      className="hidden"
                      style={{ display: "none" }}
                    />
                  </label>
                  {files.aadhaarFront && (
                    <div className="kyc-filename">Aadhaar Front: {files.aadhaarFront.name}</div>
                  )}
                </div>

                {/* Aadhaar Back Upload */}
                <div className="kyc-upload-section">
                  <label className="kyc-label">Aadhaar Card (Back) <span className="kyc-required">*</span></label>
                  <label className={`kyc-upload-label ${filePreviews.aadhaarBack ? "has-file" : ""}`}>
                    {filePreviews.aadhaarBack ? (
                      <>
                        <img src={filePreviews.aadhaarBack} alt="Aadhaar Back Preview" className="kyc-upload-preview" />
                        <button className="kyc-upload-remove" onClick={(e) => { e.preventDefault(); removeFile("aadhaarBack"); }} type="button">
                          <FiX size={13}/>
                        </button>
                      </>
                    ) : uploadingFiles.aadhaarBack ? (
                      <div className="kyc-upload-loading">
                        <FiUpload className="kyc-spin" size={20} />
                        <span>Uploading...</span>
                      </div>
                    ) : (
                      <div className="kyc-upload-placeholder">
                        <FiImage size={30} className="kyc-upload-icon" />
                        <span className="kyc-upload-text">Click to upload Aadhaar back</span>
                        <span className="kyc-upload-hint">JPG, PNG, WEBP - max 5 MB</span>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/jpg,image/webp"
                      onChange={(e) => handleFileChange("aadhaarBack", e.target.files[0])}
                      className="hidden"
                      style={{ display: "none" }}
                    />
                  </label>
                  {files.aadhaarBack && (
                    <div className="kyc-filename">Aadhaar Back: {files.aadhaarBack.name}</div>
                  )}
                </div>
              </div>

              {/* ── Section 3: Bank ── */}
              <div className="kyc-section">
                <div className="kyc-section-header">
                  <div className="kyc-section-icon"><FiCreditCard size={16}/></div>
                  <div>
                    <div className="kyc-section-title">Bank Information</div>
                    <div className="kyc-section-divider"/>
                  </div>
                </div>

                <div className="kyc-field">
                  <label className="kyc-label">Bank Name <span className="kyc-required">*</span></label>
                  <input type="text" name="bankName" className="kyc-input"
                    value={formData.bankName} onChange={handleChange}
                    placeholder="e.g. HDFC Bank, State Bank of India"
                    onFocus={() => setStep(3)}
                  />
                </div>

                <div className="kyc-field">
                  <label className="kyc-label">Bank Account Number <span className="kyc-required">*</span></label>
                  <input type="text" name="bankAccount" className="kyc-input mono"
                    value={formData.bankAccount} onChange={handleChange}
                    placeholder="Account number (min. 10 digits)"
                    onFocus={() => setStep(3)}
                  />
                </div>

                <div className="kyc-field">
                  <label className="kyc-label">IFSC Code <span className="kyc-required">*</span></label>
                  <input type="text" name="ifsc" className="kyc-input mono"
                    value={formData.ifsc} onChange={handleChange}
                    placeholder="e.g. HDFC0001234" maxLength="11"
                    onFocus={() => setStep(3)}
                  />
                  <div className="kyc-hint">11-character bank branch code</div>
                </div>

                {/* Bank Passbook Upload */}
                <div className="kyc-upload-section">
                  <label className="kyc-label">Bank Passbook <span className="kyc-required">*</span></label>
                  <label className={`kyc-upload-label ${filePreviews.bankFile ? "has-file" : ""}`}>
                    {filePreviews.bankFile ? (
                      <>
                        <img src={filePreviews.bankFile} alt="Bank Passbook Preview" className="kyc-upload-preview" />
                        <button className="kyc-upload-remove" onClick={(e) => { e.preventDefault(); removeFile("bankFile"); }} type="button">
                          <FiX size={13}/>
                        </button>
                      </>
                    ) : uploadingFiles.bankFile ? (
                      <div className="kyc-upload-loading">
                        <FiUpload className="kyc-spin" size={20} />
                        <span>Uploading...</span>
                      </div>
                    ) : (
                      <div className="kyc-upload-placeholder">
                        <FiImage size={30} className="kyc-upload-icon" />
                        <span className="kyc-upload-text">Click to upload bank passbook</span>
                        <span className="kyc-upload-hint">JPG, PNG, WEBP - max 5 MB</span>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/jpg,image/webp"
                      onChange={(e) => handleFileChange("bankFile", e.target.files[0])}
                      className="hidden"
                      style={{ display: "none" }}
                    />
                  </label>
                  {files.bankFile && (
                    <div className="kyc-filename">Passbook: {files.bankFile.name}</div>
                  )}
                </div>
              </div>

              {/* ── Notice ── */}
              <div className="kyc-notice">
                <FiLock size={15}/>
                Your documents remain private and secure. They are used only for identity verification and processing withdrawals.
              </div>

              {/* ── Submit ── */}
              <button
                type="submit"
                disabled={loading}
                className={`kyc-submit-btn ${loading ? "loading" : "ready"}`}
              >
                <FiUpload size={16}/>
                {loading ? "Submitting KYC…" : "Submit KYC Documents"}
              </button>
            </form>
          )}

          {/* Success state */}
          {success && (
            <div style={{ textAlign: "center", padding: "32px 20px" }}>
              <div style={{
                width: 72, height: 72, borderRadius: "50%",
                background: "linear-gradient(135deg,#10b981,#059669)",
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 18px", boxShadow: "0 0 0 8px rgba(16,185,129,.12)"
              }}>
                <FiCheck size={32} color="#fff"/>
              </div>
              <div style={{ fontSize: 18, fontWeight: 900, color: "#0f172a", marginBottom: 8 }}>
                KYC Submitted Successfully!
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#64748b" }}>
                Our admin team will review your documents and activate your account shortly.
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default RegisterKYC;